// Example data of Inicio (`REACT_APP_HUMAN_TASKS_FIXTURES=true`). Every name
// is fictional, ids are fake and there are no phone numbers: nothing here
// comes from a real customer. It lives outside `__fixtures__` on purpose:
// those folders are test-only (src/views/partnerOverview/__tests__/
// noBundledData.test.js), and this file is read at runtime by
// api/fixtures-human-tasks.js, only in example-data mode.

const MINUTE = 60000;

// Every task of the example sits on the same fictional channel.
export const FIXTURE_CHANNEL_ID = 'fx-channel-1';

// Expiry of a shared task by kind (spec §3, rule 2).
export const FIXTURE_EXPIRY_MS = { messaging: 7 * MINUTE, other: 25 * MINUTE };

export const FIXTURE_OUTCOMES = [
  { id: 'achieved', label: 'Se logró', positive: true },
  { id: 'visit_booked', label: 'Agendó una visita', positive: true },
  { id: 'not_achieved', label: 'No se logró', positive: false },
  { id: 'no_answer', label: 'No respondió', positive: false },
];

/**
 * Tasks the fake assigner hands out when the list needs a refill or a
 * simulated offer. Card details (D29) as the assigner would write them:
 * `label`, `priority`, `steps`, `suggestedReply` (messaging tasks only),
 * `estimatedMinutes` and the lead source of the conversation.
 */
export const FIXTURE_TASK_TEMPLATES = [
  {
    clientName: 'Lucía Hernández',
    instruction: 'Contesta a Lucía: quiere saber si puede llevar a un invitado a su clase.',
    context: 'Último mensaje hace 2 min: "¿Puedo ir con una amiga el sábado?"',
    aiCanDo: true,
    isMessaging: true,
    label: 'AGENDA',
    priority: 'normal',
    steps: ['Confirma que hay cupo el sábado', 'Pídele el nombre de su invitada'],
    suggestedReply: '¡Hola, Lucía! Sí puedes venir con una invitada el sábado. ¿Me compartes su nombre para registrarla?',
    estimatedMinutes: 3,
    lead: { source: 'CHAT', fromAds: false },
  },
  {
    clientName: 'Ricardo Navarro',
    instruction: 'Llama a Ricardo: pidió que le expliquen los planes por teléfono.',
    context: 'Escribió "mejor márquenme" después de ver los precios.',
    aiCanDo: false,
    isMessaging: false,
    label: 'LLAMADA',
    priority: 'high',
    steps: ['Llámalo hoy por la tarde', 'Explícale los planes y la promoción vigente'],
    suggestedReply: null,
    estimatedMinutes: 10,
    lead: { source: 'FB_FORM', fromAds: true },
  },
  {
    clientName: 'Paola Jiménez',
    instruction: 'Responde a Paola sobre el horario de la alberca.',
    context: 'Preguntó por el horario del domingo y nadie le ha contestado.',
    aiCanDo: true,
    isMessaging: true,
    label: 'INFORMES',
    priority: 'normal',
    steps: ['Comparte el horario del domingo'],
    suggestedReply: '¡Hola, Paola! El domingo la alberca abre de 8:00 a 14:00.',
    estimatedMinutes: 2,
    lead: { source: 'CAMPAIGN', fromAds: false },
  },
  {
    clientName: 'Hugo Salinas',
    instruction: 'Da seguimiento a Hugo: tenía una visita agendada ayer y no llegó. Pregúntale si quiere reagendar.',
    context: 'Visita agendada ayer a las 18:00. No hay mensajes desde entonces.',
    aiCanDo: true,
    isMessaging: false,
    label: 'SEGUIMIENTO',
    priority: 'normal',
    steps: ['Pregúntale si quiere reagendar', 'Si dice que sí, agenda la visita'],
    suggestedReply: null,
    estimatedMinutes: 5,
    lead: { source: 'MANUAL', fromAds: false },
  },
  {
    clientName: 'Valeria Ríos',
    instruction: 'Contesta a Valeria: pregunta si hay estacionamiento.',
    context: 'Último mensaje hace 1 min: "¿Tienen estacionamiento?"',
    aiCanDo: true,
    isMessaging: true,
    label: 'INFORMES',
    priority: 'normal',
    steps: ['Contesta si hay estacionamiento'],
    suggestedReply: '¡Hola, Valeria! Sí, tenemos estacionamiento para socios.',
    estimatedMinutes: 2,
    lead: { source: 'CHAT', fromAds: true },
  },
];

/**
 * The starting point, relative to `now`: two tasks in progress (one taken,
 * one paused; a person can hold any number, D28) and three visible.
 */
export const createInitialFixtureTasks = (now) => [
  {
    id: 'fx-task-1',
    clientId: 'fx-client-1',
    clientName: 'Mauricio Ruiz',
    instruction: 'Llama a Mauricio para confirmar su visita de mañana a las 10:00 y registra si va a venir.',
    context: 'Agendó una clase muestra para mañana a las 10:00. Todavía no confirma.',
    aiCanDo: false,
    isMessaging: false,
    status: 'taken',
    createdAt: now - 18 * MINUTE,
    takenAt: now - 6 * MINUTE,
    lastInboundAt: now - 20 * MINUTE,
    label: 'LLAMADA',
    priority: 'normal',
    steps: ['Llámalo para confirmar la visita', 'Registra si va a venir'],
    suggestedReply: null,
    estimatedMinutes: 10,
    lead: { source: 'IMPORT', fromAds: false },
  },
  {
    id: 'fx-task-2',
    clientId: 'fx-client-2',
    clientName: 'Andrea Morales',
    instruction: 'Responde a Andrea sobre los horarios de la sucursal en fin de semana.',
    context: 'Preguntó si abren el domingo. La pausaste para atender una llamada.',
    aiCanDo: true,
    isMessaging: true,
    status: 'paused',
    createdAt: now - 40 * MINUTE,
    takenAt: now - 35 * MINUTE,
    pausedAt: now - 30 * MINUTE,
    lastInboundAt: now - 41 * MINUTE,
    label: 'INFORMES',
    priority: 'normal',
    steps: ['Confirma el horario del fin de semana'],
    suggestedReply: '¡Hola, Andrea! El domingo abrimos de 8:00 a 14:00.',
    estimatedMinutes: 3,
    lead: { source: 'CHAT', fromAds: false },
  },
  {
    id: 'fx-task-3',
    clientId: 'fx-client-3',
    clientName: 'Carlos Pérez',
    instruction: 'Contesta a Carlos: preguntó por el precio de la mensualidad y no ha tenido respuesta.',
    context: 'Último mensaje hace 3 min: "¿Cuánto cuesta la mensualidad?"',
    aiCanDo: true,
    isMessaging: true,
    status: 'visible',
    createdAt: now - (FIXTURE_EXPIRY_MS.messaging - (4 * MINUTE + 32000)),
    lastInboundAt: now - 3 * MINUTE,
    label: 'VENTA',
    priority: 'high',
    steps: ['Comparte el precio con la promoción vigente', 'Ofrécele agendar una visita'],
    suggestedReply: '¡Hola, Carlos! La mensualidad tiene promoción este mes. ¿Te comparto los detalles?',
    estimatedMinutes: 5,
    lead: { source: 'FB_FORM', fromAds: true },
  },
  {
    id: 'fx-task-4',
    clientId: 'fx-client-4',
    clientName: 'Sofía Gómez',
    instruction: 'Llama a Sofía: pidió que la contacten por teléfono para resolver dudas de su inscripción.',
    context: 'Pidió una llamada hoy por la tarde.',
    aiCanDo: false,
    isMessaging: false,
    status: 'visible',
    createdAt: now - 12 * MINUTE,
    lastInboundAt: now - 13 * MINUTE,
    label: 'LLAMADA',
    priority: 'normal',
    steps: ['Llámala por la tarde', 'Resuelve sus dudas de la inscripción'],
    suggestedReply: null,
    estimatedMinutes: 8,
    lead: { source: 'CAMPAIGN', fromAds: false },
  },
  {
    id: 'fx-task-5',
    clientId: 'fx-client-5',
    clientName: 'Jorge Castillo',
    instruction: 'Da seguimiento a Jorge: faltó a su visita de ayer. Pregúntale si quiere reagendar.',
    context: 'Visita agendada ayer a las 19:00. No llegó.',
    aiCanDo: true,
    isMessaging: false,
    status: 'visible',
    createdAt: now - 7 * MINUTE,
    lastInboundAt: null,
    label: 'SEGUIMIENTO',
    priority: 'normal',
    steps: ['Pregúntale si quiere reagendar'],
    suggestedReply: null,
    estimatedMinutes: 4,
    lead: { source: 'MANUAL', fromAds: false },
  },
];

/**
 * Personal stats before this session's own closes. `week.closed` is the
 * median per day of the days worked among `closedPreviousDays` (every one of
 * the 6 here: 3, 4, 6, 6, 7, 8 → 6); the fake API dates those 6 days back
 * from today and appends today's own closes.
 */
export const FIXTURE_MY_STATS_BASE = {
  closed: 5,
  achieved: 3,
  medianTakeMs: 3 * MINUTE + 30000,
  week: { closed: 6, achieved: 4, medianTakeMs: 4 * MINUTE + 12000 },
  closedPreviousDays: [6, 8, 4, 7, 6, 3],
};

// Today per hour (spec §8: compared with the same weekday of the 4 previous
// weeks). The example day is at 16 h: `today` adds up to the 42 closed of the
// team card and is null from 17 h on; `usual` is the median of each hour and
// goes on until the end of the usual day. Up to 16 h it adds up to 38, which
// is NOT the card's baseline (37): that one is the backend's median of the 4
// previous days up to the same hour, and a sum of medians is not a median.
const TODAY_BY_HOUR = { 8: 2, 9: 4, 10: 6, 11: 7, 12: 5, 13: 3, 14: 4, 15: 6, 16: 5 };
const USUAL_BY_HOUR = { 8: 2, 9: 3, 10: 5, 11: 6, 12: 5, 13: 4, 14: 4, 15: 5, 16: 4, 17: 4, 18: 3, 19: 2, 20: 1 };
const CURRENT_HOUR = 16;

const FIXTURE_TODAY_BY_HOUR = Array.from({ length: 24 }, (_, hour) => ({
  hour,
  today: hour > CURRENT_HOUR ? null : (TODAY_BY_HOUR[hour] || 0),
  usual: USUAL_BY_HOUR[hour] || 0,
}));

export const FIXTURE_TEAM_STATS = {
  exceptions: { stuckOver2h: 1, expiredUntaken: 2, skippedByTwoOrMore: 1 },
  team: {
    closed: { value: 42, baseline: 37, baselineWeeks: 4 },
    timeToTake: { medianFromCreatedMs: 4 * MINUTE + 5000, medianFromSeenMs: 2 * MINUTE + 40000, takenBeforeExpiryRate: 0.86 },
    outcome: { declaredRate: 0.71, verifiedRate: 0.58 },
    quality: { score: 4.2, baseline: 4.0 },
    // The 5 skips of the period by reason (no names); the screen lists the first 3.
    skips: {
      rate: 0.08,
      count: 5,
      reasons: [
        { reason: 'No es de mi área', count: 2 },
        { reason: 'Estoy en otra llamada', count: 1 },
        { reason: 'El cliente ya fue atendido', count: 1 },
        { reason: 'Sin tiempo ahora', count: 1 },
      ],
    },
  },
  todayByHour: FIXTURE_TODAY_BY_HOUR,
  // 42 closed (the card), 2 expired untaken (the exceptions strip), and the
  // one skipped by 2 or more that every human turned down.
  endings: { closed: 42, expiredUntaken: 2, skipped: 3, unresolved: 1 },
  // Each agent's `outcomes` add up to their `closed`; the positive ones of
  // all agents (30 of 42) give the 71% declared of the team card.
  // `medianTimeToTakeMs` is each agent's own clock: from the first time the
  // task entered their list (the team's `medianFromSeenMs`).
  agents: [
    {
      userId: 'fx-agent-3', name: 'Raúl Ortega', tasks: 14, closed: 12, medianTimeToTakeMs: 5 * MINUTE + 20000,
      declaredRate: 0.67, verifiedRate: 0.5, qualityScore: 3.9, skipRate: 0.14,
      outcomes: { achieved: 8, notAchieved: 2, noAnswer: 2 },
    },
    {
      userId: 'fx-agent-1', name: 'Ana López', tasks: 21, closed: 19, medianTimeToTakeMs: 3 * MINUTE + 10000,
      declaredRate: 0.74, verifiedRate: 0.63, qualityScore: 4.4, skipRate: 0.05,
      outcomes: { achieved: 14, notAchieved: 3, noAnswer: 2 },
    },
    {
      userId: 'fx-agent-4', name: 'Carla Mendoza', tasks: 6, closed: 5, medianTimeToTakeMs: 2 * MINUTE + 50000,
      declaredRate: 1, verifiedRate: 0.6, qualityScore: 4.6, skipRate: 0,
      // Under 10 tasks: the screen says "pocos datos" instead of this split.
      outcomes: { achieved: 5, notAchieved: 0, noAnswer: 0 },
    },
    {
      userId: 'fx-agent-2', name: 'Bruno Díaz', tasks: 11, closed: 6, medianTimeToTakeMs: 6 * MINUTE,
      // Without verified outcome nor quality, as the backend sends them until
      // the automatic grading (part 5b): the screen says "Próximamente".
      declaredRate: 0.5, verifiedRate: null, qualityScore: null, skipRate: 0.18,
      outcomes: { achieved: 3, notAchieved: 1, noAnswer: 2 },
    },
  ],
};
